// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM rendering for Chatwell: the app shell, the invite card, the live member
 * bar, the message transcript, the typing indicator, and the composer. All
 * heavy logic (crypto, transport) lives in main.ts — this file only renders and
 * emits callbacks.
 */
import { icon } from './icons.ts';
import { mountEventDrawer } from './eventlog.ts';
import { openModal, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { renderBody, imageFilename } from './text.ts';
import { fmtBytes, fmtClock, joinNames } from './format.ts';
import type { ChatMessage, Member, Status } from './types.ts';

export interface UICallbacks {
  onSend: (text: string) => void;
  onInput: () => void;
  onPickImage: (files: File[]) => void;
  onCopyLink: () => void;
  onShareLink: () => void;
  onToggleQr: () => void;
  onNameChange: (name: string) => void;
  onExport: () => void;
  onClearMessages: () => void;
  onLeave: () => void;
  onSaveImage: (id: string) => void;
  onShareImage: (id: string) => void;
  onCopyText: (id: string) => void;
}

export class AppUI {
  private root: HTMLElement;
  private cb: UICallbacks;
  private logEl!: HTMLElement;
  private composeEl!: HTMLTextAreaElement;
  private membersEl!: HTMLElement;
  private typingEl!: HTMLElement;
  private stateDot!: HTMLElement;
  private stateText!: HTMLElement;
  private hereEl!: HTMLElement;
  private linkInput!: HTMLInputElement;
  private qrWrap!: HTMLElement;
  private qrImg!: HTMLImageElement;
  private sasWords!: HTMLElement;
  private nameInput!: HTMLInputElement;
  private fileInput!: HTMLInputElement;
  private drawer!: HTMLElement;
  private lastAuthorId: string | null = null;

  constructor(root: HTMLElement, cb: UICallbacks) {
    this.root = root;
    this.cb = cb;
    this.render();
  }

  private render(): void {
    this.root.innerHTML = `
      <div class="app-shell">
        <header class="topbar">
          <div class="brand">
            <span class="brand-mark">${icon('chat')}</span>
            <span class="brand-name">Chatwell</span>
          </div>
          <nav class="topnav">
            <button class="nav-btn" id="nav-how" type="button">${icon('help')}<span>How it works</span></button>
            <button class="nav-btn" id="nav-privacy" type="button">${icon('shield')}<span>Privacy</span></button>
            <button class="nav-btn" id="nav-about" type="button">${icon('info')}<span>About</span></button>
            <button class="nav-btn" id="nav-log" type="button">${icon('logDrawer')}<span>Log</span></button>
          </nav>
        </header>

        <button class="trust-banner" id="trust-banner" type="button">
          ${icon('lock')}
          <span><strong>End-to-end encrypted, nothing stored.</strong> Messages go straight browser-to-browser over an encrypted mesh. No account, no server copy.</span>
        </button>

        <main class="main-content">
          <section class="card invite-card">
            <div class="invite-head">
              <div>
                <h1 class="card-title">Your private room</h1>
                <p class="card-sub">Share this link with the people you want in the room. It carries the room and its passphrase in the part of the URL browsers never send to a server.</p>
              </div>
            </div>

            <label class="field-label" for="name-input">Your display name</label>
            <div class="name-row">
              <span class="name-ic">${icon('edit')}</span>
              <input class="text-input" id="name-input" type="text" maxlength="24" autocomplete="off" spellcheck="false" placeholder="Pick a name" />
            </div>

            <label class="field-label" for="link-input" style="margin-top:14px">Invite link</label>
            <div class="link-row">
              <input class="link-input" id="link-input" readonly value="" aria-label="Invite link" />
              <button class="btn btn-sm" id="btn-copy" type="button" title="Copy link">${icon('copy')}</button>
              <button class="btn btn-sm" id="btn-share" type="button" title="Share link">${icon('share')}</button>
              <button class="btn btn-sm" id="btn-qr" type="button" title="Show QR">${icon('qr')}</button>
            </div>

            <div class="qr-wrap" id="qr-wrap" hidden>
              <div class="qr-box"><img id="qr-img" alt="Invite QR code" /></div>
              <span class="qr-cap">${icon('qr')} Scan to join on another device</span>
            </div>

            <div class="sas-box">
              <div class="sas-head">${icon('key')} <span>Safety words — everyone in the room should see the same four:</span></div>
              <div class="sas-words" id="sas-words"></div>
            </div>
          </section>

          <section class="card room-card">
            <div class="room-bar">
              <span class="status-dot" id="state-dot" data-status="connecting"></span>
              <span class="status-text" id="state-text">Connecting…</span>
              <span class="here" id="here">${icon('users')} <span id="here-n">1 here</span></span>
              <div class="room-actions">
                <button class="btn btn-sm" id="btn-export" type="button" title="Export transcript">${icon('download')}<span>Export</span></button>
                <button class="btn btn-sm" id="btn-clear" type="button" title="Clear messages on this device">${icon('trash')}</button>
                <button class="btn btn-sm btn-danger" id="btn-leave" type="button" title="Leave room">${icon('power')}<span>Leave</span></button>
              </div>
            </div>
            <div class="members" id="members"></div>

            <div class="chat-log" id="chat-log" aria-live="polite">
              <div class="log-empty" id="log-empty">
                <span class="log-empty-ic">${icon('chat')}</span>
                <p>No messages yet. You’re the first one here — share the invite link above to bring people in, then say hello.</p>
              </div>
            </div>

            <div class="typing" id="typing" hidden></div>

            <form class="composer" id="composer">
              <button class="icon-round" id="btn-image" type="button" title="Send an image" aria-label="Send an image">${icon('image')}</button>
              <textarea class="compose-input" id="compose" rows="1" placeholder="Message…" aria-label="Message" maxlength="8000"></textarea>
              <button class="icon-round send" id="btn-send" type="submit" title="Send" aria-label="Send">${icon('send')}</button>
              <input type="file" id="file-input" accept="image/*" hidden />
            </form>
            <div class="compose-hint muted small">Press <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line · drop or paste an image</div>
          </section>
        </main>

        <footer class="site-footer">
          <div class="footer-inner">
            Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
            · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
          </div>
        </footer>

        <aside class="drawer" id="drawer" aria-hidden="true"><div id="drawer-body"></div></aside>
      </div>`;

    // Element handles
    this.logEl = this.q('#chat-log');
    this.composeEl = this.q('#compose');
    this.membersEl = this.q('#members');
    this.typingEl = this.q('#typing');
    this.stateDot = this.q('#state-dot');
    this.stateText = this.q('#state-text');
    this.hereEl = this.q('#here-n');
    this.linkInput = this.q('#link-input');
    this.qrWrap = this.q('#qr-wrap');
    this.qrImg = this.q('#qr-img');
    this.sasWords = this.q('#sas-words');
    this.nameInput = this.q('#name-input');
    this.fileInput = this.q('#file-input');
    this.drawer = this.q('#drawer');

    this.wire();
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.root.querySelector(sel) as T;
  }

  private wire(): void {
    this.q('#nav-how').addEventListener('click', () => openModal('How Chatwell works', howItWorksHtml()));
    this.q('#nav-privacy').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    this.q('#nav-about').addEventListener('click', () => openModal('About Chatwell', aboutHtml()));
    this.q('#trust-banner').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));

    // Event-log drawer
    const drawerBody = this.q('#drawer-body');
    mountEventDrawer(drawerBody);
    this.q('#nav-log').addEventListener('click', () => this.toggleDrawer());
    drawerBody.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#drawer-close')) this.toggleDrawer(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('open')) this.toggleDrawer(false);
    });

    // Invite actions
    this.q('#btn-copy').addEventListener('click', () => this.cb.onCopyLink());
    this.q('#btn-share').addEventListener('click', () => this.cb.onShareLink());
    this.q('#btn-qr').addEventListener('click', () => this.cb.onToggleQr());
    this.linkInput.addEventListener('focus', () => this.linkInput.select());

    // Name
    this.nameInput.addEventListener('change', () => this.cb.onNameChange(this.nameInput.value));
    this.nameInput.addEventListener('blur', () => this.cb.onNameChange(this.nameInput.value));

    // Room actions
    this.q('#btn-export').addEventListener('click', () => this.cb.onExport());
    this.q('#btn-clear').addEventListener('click', () => this.cb.onClearMessages());
    this.q('#btn-leave').addEventListener('click', () => this.cb.onLeave());

    // Composer
    const form = this.q<HTMLFormElement>('#composer');
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    this.composeEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    });
    this.composeEl.addEventListener('input', () => {
      this.autogrow();
      this.cb.onInput();
    });
    this.composeEl.addEventListener('paste', (e) => this.onPaste(e));

    // Image picker + drag/drop
    this.q('#btn-image').addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', () => {
      const files = this.fileInput.files ? Array.from(this.fileInput.files) : [];
      if (files.length) this.cb.onPickImage(files);
      this.fileInput.value = '';
    });
    const dropZone = this.q('.room-card');
    ['dragover', 'dragenter'].forEach((ev) =>
      dropZone.addEventListener(ev, (e) => {
        e.preventDefault();
        dropZone.classList.add('drag');
      }),
    );
    ['dragleave', 'drop'].forEach((ev) =>
      dropZone.addEventListener(ev, (e) => {
        if (ev === 'dragleave' && dropZone.contains((e as DragEvent).relatedTarget as Node)) return;
        dropZone.classList.remove('drag');
      }),
    );
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      const dt = (e as DragEvent).dataTransfer;
      const files = dt ? Array.from(dt.files).filter((f) => f.type.startsWith('image/')) : [];
      if (files.length) this.cb.onPickImage(files);
    });

    // Delegated message-action clicks
    this.logEl.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
      if (!btn) return;
      const id = btn.dataset.id || '';
      const act = btn.dataset.act;
      if (act === 'save') this.cb.onSaveImage(id);
      else if (act === 'share') this.cb.onShareImage(id);
      else if (act === 'copy') this.cb.onCopyText(id);
    });
  }

  private submit(): void {
    const text = this.composeEl.value;
    if (!text.trim()) return;
    this.cb.onSend(text.trim());
    this.composeEl.value = '';
    this.autogrow();
  }

  private onPaste(e: ClipboardEvent): void {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          this.cb.onPickImage([file]);
          return;
        }
      }
    }
  }

  private autogrow(): void {
    const el = this.composeEl;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  private toggleDrawer(force?: boolean): void {
    const open = force ?? !this.drawer.classList.contains('open');
    this.drawer.classList.toggle('open', open);
    this.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  // ── public API ─────────────────────────────────────────────────────────────
  setName(name: string): void {
    this.nameInput.value = name;
  }
  getName(): string {
    return this.nameInput.value;
  }
  setInviteLink(url: string): void {
    this.linkInput.value = url;
  }
  setQr(dataUrl: string): void {
    this.qrImg.src = dataUrl;
  }
  toggleQr(): void {
    this.qrWrap.hidden = !this.qrWrap.hidden;
  }
  setSas(words: string[]): void {
    this.sasWords.innerHTML = '';
    for (const w of words) {
      const s = document.createElement('span');
      s.className = 'sas-word';
      s.textContent = w;
      this.sasWords.appendChild(s);
    }
  }
  setStatus(status: Status, here = 1): void {
    this.stateDot.dataset.status = status === 'live' ? 'ready' : status === 'error' ? 'error' : status === 'waiting' ? 'waiting' : 'waiting';
    const labels: Record<Status, string> = {
      idle: 'Not connected',
      connecting: 'Connecting…',
      waiting: 'Waiting for others to join…',
      live: here > 1 ? 'Room live' : 'You’re here — waiting for others',
      error: 'Connection problem',
    };
    this.stateText.textContent = labels[status];
    this.hereEl.textContent = `${here} here`;
  }

  renderMembers(members: Member[], selfId: string): void {
    this.membersEl.innerHTML = '';
    for (const m of members) {
      const chip = document.createElement('span');
      chip.className = 'member-chip';
      const isSelf = m.id === selfId;
      chip.innerHTML = `<span class="member-dot" style="background:hsl(${m.hue} 70% 60%)"></span><span class="member-name"></span>${isSelf ? '<span class="you">you</span>' : ''}`;
      (chip.querySelector('.member-name') as HTMLElement).textContent = m.name;
      this.membersEl.appendChild(chip);
    }
  }

  addMessage(msg: ChatMessage, member?: Member): void {
    this.q('#log-empty').hidden = true;
    const atBottom = this.isAtBottom();

    const row = document.createElement('div');
    row.className = 'msg-row';
    row.dataset.self = msg.self ? '1' : '0';
    row.dataset.id = msg.id;
    const hue = member?.hue ?? 174;

    const showAuthor = !msg.self && this.lastAuthorId !== msg.authorId;
    this.lastAuthorId = msg.authorId;

    const authorHtml = showAuthor
      ? `<div class="msg-author" style="color:hsl(${hue} 70% 68%)"></div>`
      : '';

    let bodyHtml: string;
    if (msg.kind === 'image' && msg.blobUrl) {
      const cap = imageFilename(msg.imageName, msg.mime, msg.at);
      bodyHtml = `
        <div class="msg-image">
          <img src="${msg.blobUrl}" alt="Shared image" loading="lazy" />
          <div class="msg-image-bar">
            <span class="msg-image-name"></span>
            <span class="msg-image-acts">
              <button class="mini-act" data-act="save" data-id="${msg.id}" title="Save image">${icon('download')}</button>
              <button class="mini-act" data-act="share" data-id="${msg.id}" title="Share image">${icon('share')}</button>
            </span>
          </div>
        </div>`;
      row.dataset.cap = cap;
      row.dataset.size = String(msg.size ?? 0);
    } else {
      bodyHtml = `<div class="msg-text">${renderBody(msg.text ?? '')}</div>
        <button class="mini-act msg-copy" data-act="copy" data-id="${msg.id}" title="Copy text">${icon('copy')}</button>`;
    }

    const stateHtml = msg.self
      ? `<span class="msg-state" data-state="${msg.state ?? 'sent'}">${msg.state === 'error' ? '!' : icon('check')}</span>`
      : '';

    row.innerHTML = `
      <div class="bubble" style="${!msg.self ? `border-left-color:hsl(${hue} 60% 55%)` : ''}">
        ${authorHtml}
        ${bodyHtml}
        <div class="msg-meta"><span class="msg-time">${fmtClock(new Date(msg.at))}</span>${stateHtml}</div>
      </div>`;

    if (showAuthor) (row.querySelector('.msg-author') as HTMLElement).textContent = msg.authorName;
    const nameEl = row.querySelector('.msg-image-name') as HTMLElement | null;
    if (nameEl) nameEl.textContent = `${imageFilename(msg.imageName, msg.mime, msg.at)} · ${fmtBytes(msg.size ?? 0)}`;

    this.logEl.appendChild(row);
    if (atBottom || msg.self) this.scrollToBottom();
  }

  updateMessageState(id: string, state: ChatMessage['state']): void {
    const row = this.logEl.querySelector(`.msg-row[data-id="${CSS.escape(id)}"]`);
    const el = row?.querySelector('.msg-state') as HTMLElement | null;
    if (el) {
      el.dataset.state = state ?? 'sent';
      el.innerHTML = state === 'error' ? '!' : icon('check');
    }
  }

  setTyping(names: string[]): void {
    if (names.length === 0) {
      this.typingEl.hidden = true;
      this.typingEl.textContent = '';
      return;
    }
    const label =
      names.length > 3 ? 'Several people are typing' : `${joinNames(names)} ${names.length === 1 ? 'is' : 'are'} typing`;
    this.typingEl.hidden = false;
    this.typingEl.innerHTML = `<span class="typing-dots"><i></i><i></i><i></i></span> <span class="typing-label"></span>`;
    (this.typingEl.querySelector('.typing-label') as HTMLElement).textContent = `${label}…`;
    if (this.isAtBottom()) this.scrollToBottom();
  }

  clearMessages(): void {
    this.logEl.querySelectorAll('.msg-row').forEach((n) => n.remove());
    this.lastAuthorId = null;
    this.q('#log-empty').hidden = false;
  }

  focusCompose(): void {
    this.composeEl.focus();
  }

  private isAtBottom(): boolean {
    return this.logEl.scrollTop + this.logEl.clientHeight >= this.logEl.scrollHeight - 80;
  }
  private scrollToBottom(): void {
    this.logEl.scrollTop = this.logEl.scrollHeight;
  }
}
